import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Button, Tooltip, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Typography, ToggleButton, ToggleButtonGroup,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Divider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectProperties,
    selectGlobalProperty,
    selectLocalProperty,
    setLocalProperty
} from '../../redux/propertySlice';
import { GenerateBedCatCal } from '../../Api/PropertyMst/Api';
import axiosInstance from '../../lib/axios';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line, Doughnut, Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
    ChartTooltip, Legend, Filler, ChartDataLabels
);

const COLORS = {
    bg: '#0b0b14',
    bgSoft: '#100f1c',
    card: '#161528',
    cardBorder: '#2a2942',
    borderLight: '#3c3a5c',
    primary: '#635BFF',
    primaryDark: '#4f47e6',
    primaryLight: '#8f89ff',
    primaryGlow: 'rgba(99,91,255,0.35)',
    success: '#34d399',
    warning: '#fbbf24',
    danger: '#f87171',
    cyan: '#22d3ee',
    pink: '#e879f9',
    text: '#f6f6fb',
    textSecondary: '#b9b7e8',
    textMuted: '#8a87b3'
};

const GRADIENT_PRIMARY = `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`;

const PALETTE = [COLORS.primary, COLORS.pink, COLORS.cyan, COLORS.warning];

const cardSx = {
    background: `linear-gradient(180deg, ${COLORS.card} 0%, ${COLORS.bgSoft} 100%)`,
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: '12px',
    color: COLORS.text,
    height: '100%',
    transition: 'transform 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        borderColor: COLORS.borderLight,
        boxShadow: `0 10px 28px -8px ${COLORS.primaryGlow}`
    }
};

const headerSx = {
    px: 2.4, py: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1
};

const darkFieldSx = {
    '& .MuiOutlinedInput-root': {
        color: COLORS.text,
        background: COLORS.bg,
        borderRadius: '10px',
        '& fieldset': { borderColor: COLORS.borderLight },
        '&:hover fieldset': { borderColor: COLORS.primary },
        '&.Mui-focused fieldset': { borderColor: COLORS.primary }
    },
    '& .MuiInputLabel-root': { color: COLORS.textMuted },
    '& .MuiSvgIcon-root': { color: COLORS.textMuted }
};

const legendSx = { usePointStyle: true, padding: 12, color: COLORS.textSecondary, font: { size: 11 } };
const tooltipTheme = { backgroundColor: COLORS.card, titleColor: COLORS.text, bodyColor: COLORS.textSecondary, borderColor: COLORS.cardBorder, borderWidth: 1, cornerRadius: 10, padding: 12 };

const pctBadge = (n) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700,
    background: n >= 50 ? 'rgba(248,113,113,0.2)' : n >= 20 ? 'rgba(251,191,36,0.2)' : 'rgba(52,211,153,0.2)',
    color: n >= 50 ? '#fca5a5' : n >= 20 ? '#fcd34d' : '#6ee7b7'
});

const KPI_ACCENTS = [COLORS.primary, '#fbbf24', '#e879f9', '#34d399', '#22d3ee', '#f87171', '#38bdf8'];

export default function Occupancy() {
    const dispatch = useDispatch();
    const properties = useSelector(selectProperties);
    const globalProperty = useSelector(selectGlobalProperty);
    const localProperty = useSelector(state => selectLocalProperty(state, 'occupancy'));
    const selectedProperty = localProperty || globalProperty;
    const [dateFrom, setDateFrom] = useState(dayjs());
    const [dateTo, setDateTo] = useState(dayjs().add(11, 'months'));
    const [bedTypeFilter, setBedTypeFilter] = useState('all');
    const [trendMetric, setTrendMetric] = useState('occupancy');
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [months, setMonths] = useState([]);

    const handleDateFromChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            const startDate = newDate.startOf('month');
            setDateFrom(startDate);
            setDateTo(startDate.add(11, 'months'));
        }
    };

    const handleDateToChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            setDateTo(newDate.startOf('month'));
        }
    };

    const handleDropdownChange = (event) => {
        const selectedId = event.target.value;
        dispatch(setLocalProperty({ componentId: 'occupancy', propertyId: selectedId }));
    };

    useEffect(() => {
        if (selectedProperty) {
            fetchData();
        }
    }, [selectedProperty]);

    const fetchData = async () => {
        if (!selectedProperty) {
            toast.error('Please select a property');
            return;
        }

        const monthDiff = dateTo.diff(dateFrom, 'month');
        if (monthDiff < 0) {
            toast.warning("To date selection can't earlier than from date.");
            return;
        }
        if (monthDiff > 12) {
            toast.warning("Date Range Can't exceed 12 months.");
            return;
        }

        try {
            setIsLoading(true);

            const payload = {
                PropId: selectedProperty,
                BookDtFrom: dateFrom.format('YYYY-MM-DD'),
                CheckoutDt: dateTo.format('YYYY-MM-DD'),
                Flag: 'Summary'
            };

            const response = await GenerateBedCatCal(payload);

            if (response?.data?.Status === 0) {
                const data = response.data.Data?.BedCatCalList || [];
                setApiData(data);

                const labels = [];
                let current = dateFrom.startOf('month');
                while (current.isBefore(dateTo) || current.isSame(dateTo, 'month')) {
                    labels.push({ monthName: current.format('MMMM'), label: current.format('MMM-YY') });
                    current = current.add(1, 'month');
                }
                setMonths(labels);
            } else {
                toast.info(response?.data?.Message || 'Error fetching data.');
                setApiData([]);
                setMonths([]);
            }
        } catch (error) {
            toast.error('Failed to fetch data.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = async () => {
        if (!selectedProperty) {
            toast.error('Please select a property');
            return;
        }
        try {
            setIsRefreshing(true);
            const payload = { PropId: selectedProperty, Flag: '' };
            await Promise.all([
                axiosInstance.post('BedCalendar/InsertBedCat', payload),
                axiosInstance.post('BedCalendar/InsertBedCatCal', payload),
                axiosInstance.post('BedCalendar/UpdateBedCat', payload),
                axiosInstance.post('BedCalendar/UpdateBedCatCal_Data', payload),
            ]);
            toast.success('Latest data retrieved successfully.');
            await fetchData();
        } catch (error) {
            toast.error('Error while refreshing the data.');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Aggregate raw API rows -> per Room/Bed-type series aligned to `months`
    const series = useMemo(() => {
        if (!apiData.length || !months.length) return [];

        const grouped = {};
        apiData.forEach(item => {
            const key = `${item.RoomTypeName}-${item.RoomBedTypeName}`;
            if (!grouped[key]) {
                grouped[key] = { roomType: item.RoomTypeName, bedType: item.RoomBedTypeName, byMonth: {} };
            }
            grouped[key].byMonth[item.MonthNm] = {
                total: (item.BedToSale || 0) + (item.ExtraBed || 0),
                booked: item.BookingBeds || 0,
                occupied: item.OccBeds || 0,
                balance: item.BalBed || 0,
                partial: item.PartialBeds || 0
            };
        });

        return Object.values(grouped).map(g => {
            const totalArr = [], bookedArr = [], occupiedArr = [], balanceArr = [], partialArr = [];
            months.forEach(m => {
                const rec = g.byMonth[m.monthName] || { total: 0, booked: 0, occupied: 0, balance: 0, partial: 0 };
                totalArr.push(rec.total);
                bookedArr.push(rec.booked);
                occupiedArr.push(rec.occupied);
                balanceArr.push(rec.balance);
                partialArr.push(rec.partial);
            });
            return { roomType: g.roomType, bedType: g.bedType, totalArr, bookedArr, occupiedArr, balanceArr, partialArr };
        });
    }, [apiData, months]);

    const bedTypeOptions = useMemo(() => Array.from(new Set(series.map(s => s.bedType))), [series]);

    const filteredSeries = useMemo(
        () => (bedTypeFilter === 'all' ? series : series.filter(s => s.bedType === bedTypeFilter)),
        [series, bedTypeFilter]
    );

    const totalsAt = (idx) => {
        let total = 0, occ = 0, booked = 0, bal = 0, partial = 0;
        filteredSeries.forEach(s => {
            total += s.totalArr[idx] || 0;
            occ += s.occupiedArr[idx] || 0;
            booked += s.bookedArr[idx] || 0;
            bal += s.balanceArr[idx] || 0;
            partial += s.partialArr[idx] || 0;
        });
        // "Total Occupancy %" = (Occupied + Partial) ÷ Total. `occPct` is kept
        // separately as the raw Occupied-only % (used by the donut mix chart).
        return {
            total, occ, booked, bal, partial,
            occPct: total ? (occ / total) * 100 : 0,
            pct: total ? ((occ + partial) / total) * 100 : 0,
            availPct: total ? (bal / total) * 100 : 0,
            bookedPct: total ? (booked / total) * 100 : 0,
            partialPct: total ? (partial / total) * 100 : 0
        };
    };

    const hasData = months.length > 0 && series.length > 0;
    const lastIdx = months.length ? months.length - 1 : 0;
    const lastTotals = hasData ? totalsAt(lastIdx) : { total: 0, occ: 0, booked: 0, bal: 0, partial: 0, occPct: 0, pct: 0, availPct: 0, bookedPct: 0, partialPct: 0 };
    const firstTotals = hasData ? totalsAt(0) : lastTotals;

    const { avgOcc, peakPct, peakMonth } = useMemo(() => {
        if (!hasData) return { avgOcc: 0, peakPct: 0, peakMonth: '' };
        let sumOccPartial = 0, sumTotal = 0, peak = 0, peakM = '';
        months.forEach((m, i) => {
            const t = totalsAt(i);
            sumOccPartial += t.occ + t.partial; sumTotal += t.total;
            if (t.pct > peak) { peak = t.pct; peakM = m.label; }
        });
        return { avgOcc: sumTotal ? (sumOccPartial / sumTotal) * 100 : 0, peakPct: peak, peakMonth: peakM };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [months, filteredSeries, hasData]);

    // Chart datasets
    const trendChartData = useMemo(() => {
        if (!hasData) return null;
        const labels = months.map(m => m.label);
        const datasets = filteredSeries.map((s, idx) => {
            let data, label, isPct = true;
            if (trendMetric === 'occupancy') {
                data = s.occupiedArr.map((o, i) => (s.totalArr[i] ? +((o / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Occ %`;
            } else if (trendMetric === 'available') {
                data = s.balanceArr.map((b, i) => (s.totalArr[i] ? +((b / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Avail %`;
            } else if (trendMetric === 'bookedpct') {
                data = s.bookedArr.map((b, i) => (s.totalArr[i] ? +((b / s.totalArr[i]) * 100).toFixed(1) : 0));
                label = `${s.bedType} Booked %`;
            } else {
                data = s.occupiedArr; label = `${s.bedType} Occupied`; isPct = false;
            }
            const color = PALETTE[idx % PALETTE.length];
            return {
                label, data, borderColor: color, backgroundColor: `${color}28`, borderWidth: 2.5,
                tension: 0.4, pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: color, fill: true,
                datalabels: { display: true, color, font: { size: 10, weight: '700' }, align: 'top', offset: 4, formatter: v => (isPct ? `${v}%` : v) }
            };
        });
        return { labels, datasets };
    }, [filteredSeries, months, trendMetric, hasData]);

    const trendOptions = {
        responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 18 } },
        plugins: { legend: { position: 'bottom', labels: legendSx }, tooltip: tooltipTheme, datalabels: { display: true } },
        scales: {
            y: { beginAtZero: true, grid: { color: 'rgba(45,38,64,0.8)' }, ticks: { color: COLORS.textMuted, callback: v => (trendMetric === 'occupied' ? v : `${v}%`) } },
            x: { grid: { display: false }, ticks: { color: COLORS.textMuted } }
        }
    };

    const donutData = useMemo(() => {
        if (!hasData) return null;
        const free = lastTotals.total - lastTotals.occ;
        return {
            labels: [`Occupied ${lastTotals.pct.toFixed(1)}%`, `Available ${lastTotals.availPct.toFixed(1)}%`, `Booked ${lastTotals.bookedPct.toFixed(1)}%`],
            datasets: [{ data: [lastTotals.occ, free, lastTotals.booked], backgroundColor: [COLORS.warning, COLORS.success, COLORS.pink], borderWidth: 0, hoverOffset: 8 }]
        };
    }, [lastTotals, hasData]);

    const donutOptions = {
        responsive: true, maintainAspectRatio: false, cutout: '62%',
        plugins: {
            legend: { position: 'bottom', labels: legendSx },
            tooltip: { ...tooltipTheme, callbacks: { label: c => ` ${c.label.split(' ')[0]}: ${c.raw.toLocaleString()}` } },
            datalabels: { color: '#fff', font: { size: 11, weight: '700' }, formatter: (v) => (lastTotals.total ? (v / lastTotals.total) * 100 >= 5 ? `${((v / lastTotals.total) * 100).toFixed(0)}%` : '' : '') }
        }
    };

    const barOccupiedData = useMemo(() => {
        if (!hasData) return null;
        return {
            labels: months.map(m => m.label),
            datasets: filteredSeries.map((s, idx) => ({
                label: s.bedType, data: s.occupiedArr, backgroundColor: PALETTE[idx % PALETTE.length],
                borderRadius: 5, borderSkipped: false,
                datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 0 ? v : '') }
            }))
        };
    }, [filteredSeries, months, hasData]);

    const barBookedAvailData = useMemo(() => {
        if (!hasData) return null;
        const booked = months.map((m, i) => totalsAt(i).booked);
        const avail = months.map((m, i) => totalsAt(i).bal);
        return {
            labels: months.map(m => m.label),
            datasets: [
                { label: 'Booked', data: booked, backgroundColor: COLORS.pink, borderRadius: 5, datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 0 ? v : '') } },
                { label: 'Available', data: avail, backgroundColor: COLORS.success, borderRadius: 5, datalabels: { display: true, color: '#fff', font: { size: 9, weight: '700' }, anchor: 'end', align: 'start', formatter: v => (v > 5 ? v : '') } }
            ]
        };
    }, [months, filteredSeries, hasData]);

    const barOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { ...legendSx, font: { size: 10 } } }, tooltip: tooltipTheme, datalabels: { display: true } },
        scales: {
            y: { beginAtZero: true, grid: { color: 'rgba(45,38,64,0.7)' }, ticks: { color: COLORS.textMuted } },
            x: { grid: { display: false }, ticks: { color: COLORS.textMuted, font: { size: 9 } } }
        }
    };

    const pieBedTypeData = useMemo(() => {
        if (!hasData) return null;
        const byType = {};
        series.forEach(s => { byType[s.bedType] = (byType[s.bedType] || 0) + s.occupiedArr.reduce((a, b) => a + b, 0); });
        const labels = Object.keys(byType);
        const total = Object.values(byType).reduce((a, b) => a + b, 0) || 1;
        return {
            labels: labels.map(l => `${l} ${((byType[l] / total) * 100).toFixed(0)}%`),
            datasets: [{ data: labels.map(l => byType[l] || 0.01), backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 0 }]
        };
    }, [series, hasData]);

    const pieOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { ...legendSx, font: { size: 10 } } },
            tooltip: { ...tooltipTheme, callbacks: { label: c => ` ${c.label.split(' ')[0]}: ${c.raw.toLocaleString()}` } },
            datalabels: { color: '#fff', font: { size: 12, weight: '700' } }
        }
    };

    const statusFor = (label, value, kind) => {
        let level = 'good';
        if (kind === 'occ') level = value >= 50 ? 'good' : value >= 20 ? 'warning' : 'critical';
        if (kind === 'avail') level = value > 70 ? 'critical' : value > 40 ? 'warning' : 'good';
        if (kind === 'booked') level = value > 15 ? 'good' : value > 5 ? 'warning' : 'critical';
        const colorMap = { good: COLORS.success, warning: COLORS.warning, critical: COLORS.danger };
        return { label, value, color: colorMap[level] };
    };

    return (
        <Box sx={{ background: COLORS.bg, minHeight: '100vh', color: COLORS.text, py: { xs: 1.5, md: 2 }, px: 3 }} >
            <ToastContainer />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '13px', background: GRADIENT_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', boxShadow: `0 6px 18px -4px ${COLORS.primaryGlow}`, flexShrink: 0 }}>🛏️</Box>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                        Inventory(Beds/Units) Analysis Dashboard
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, fontWeight: 500 }}>
                        AI-assisted occupancy intelligence, live from your booking data
                    </Typography>
                </Box>
            </Box>

            {/* -------- Filter bar: reused from BedCategorySummary -------- */}
            <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 190, ...darkFieldSx }}>
                        <InputLabel id="property-select-label">Property</InputLabel>
                        <Select
                            labelId="property-select-label"
                            value={selectedProperty || ''}
                            onChange={handleDropdownChange}
                            label="Property"
                            sx={{ height: '42px' }}
                        >
                            {properties?.map((item) => (
                                <MenuItem key={item.Id} value={item.Id}>{item.Name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <DatePicker
                                label="From-Date"
                                value={dateFrom}
                                onChange={handleDateFromChange}
                                format="MM/YYYY"
                                views={['month', 'year']}
                                sx={{ width: 150, ...darkFieldSx }}
                            />
                            <DatePicker
                                label="To-Date"
                                value={dateTo}
                                onChange={handleDateToChange}
                                format="MM/YYYY"
                                views={['month', 'year']}
                                minDate={dateFrom}
                                maxDate={dateFrom ? dateFrom.add(12, 'months') : null}
                                sx={{ width: 150, ...darkFieldSx }}
                            />
                        </Box>
                    </LocalizationProvider>

                    <Tooltip title="Click to see data" arrow>
                        <span>
                            <Button
                                variant="contained"
                                onClick={fetchData}
                                disabled={isLoading || isRefreshing}
                                sx={{ height: '42px', minWidth: 118, whiteSpace: 'nowrap', background: GRADIENT_PRIMARY, borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: `0 4px 16px -4px ${COLORS.primaryGlow}`, '&:hover': { background: GRADIENT_PRIMARY, boxShadow: `0 6px 22px -4px ${COLORS.primaryGlow}` }, '&.Mui-disabled': { background: 'rgba(99,91,255,0.25)', color: 'rgba(255,255,255,0.6)' } }}
                            >
                                {isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Get Data'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Refresh latest data" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                onClick={handleRefresh}
                                disabled={isLoading || isRefreshing}
                                startIcon={isRefreshing ? <CircularProgress size={16} sx={{ color: COLORS.primaryLight }} /> : null}
                                sx={{ height: '42px', minWidth: 118, whiteSpace: 'nowrap', borderColor: COLORS.borderLight, color: COLORS.textSecondary, borderRadius: '10px', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: COLORS.primary, background: 'rgba(99,91,255,0.08)', color: COLORS.text }, '&.Mui-disabled': { borderColor: COLORS.cardBorder, color: COLORS.textMuted } }}
                            >
                                {isRefreshing ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </span>
                    </Tooltip>

                    {bedTypeOptions.length > 1 && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={bedTypeFilter}
                            onChange={(e, v) => v && setBedTypeFilter(v)}
                            sx={{ ml: 'auto', background: COLORS.bg, borderRadius: '8px', p: '3px' }}
                        >
                            <ToggleButton value="all" sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.4, '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>All Beds</ToggleButton>
                            {bedTypeOptions.map(bt => (
                                <ToggleButton key={bt} value={bt} sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.4, '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>{bt}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Paper>

            {!hasData ? (
                <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
                    {(isLoading || isRefreshing) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.6 }}>
                            <CircularProgress size={34} sx={{ color: COLORS.primary }} />
                            <Typography sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>
                                {isRefreshing ? 'Refreshing the latest booking data…' : 'Loading occupancy data…'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ fontSize: '2rem' }}>📊</Box>
                            <Typography sx={{ color: COLORS.text, fontWeight: 700 }}>No data loaded yet</Typography>
                            <Typography sx={{ color: COLORS.textMuted, fontSize: '0.85rem', maxWidth: 420 }}>
                                Select a property and date range above, then click <b>Get Data</b> to build the occupancy dashboard.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            ) : (
                <>
                    {(isLoading || isRefreshing) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.6, py: 1, borderRadius: '10px', background: 'rgba(99,91,255,0.08)', border: `1px solid ${COLORS.primaryGlow}` }}>
                            <CircularProgress size={16} sx={{ color: COLORS.primary }} />
                            <Typography sx={{ fontSize: '0.78rem', color: COLORS.primaryLight, fontWeight: 600 }}>
                                {isRefreshing ? 'Refreshing latest booking data…' : 'Loading…'}
                            </Typography>
                        </Box>
                    )}

                    {/* -------- AI recommendation banner -------- */}
                    <Paper elevation={0} sx={{ background: `linear-gradient(135deg, rgba(99,91,255,0.16), rgba(232,121,249,0.08))`, border: `1px solid ${COLORS.primaryGlow}`, borderRadius: '14px', px: 2, py: 1, mb: 1.5, position: 'relative', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: COLORS.primaryLight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                🧠 AI Recommendation →
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: '0.85rem',
                                    color: COLORS.textSecondary,
                                    lineHeight: 1.6,
                                    flex: 1,
                                }}
                            >
                                Occupancy moves by{' '}
                                <b>{(lastTotals.pct - firstTotals.pct).toFixed(1)} points</b> from{' '}
                                {months[0]?.label} to {months[lastIdx]?.label}. Available inventory sits at{' '}
                                <b>{lastTotals.availPct.toFixed(0)}%</b> while booked pipeline is{' '}
                                <b>{lastTotals.bookedPct.toFixed(1)}%</b> in the final month —{' '}
                                {lastTotals.bal.toLocaleString()} beds remain free to sell.
                            </Typography>
                        </Box>
                    </Paper>

                    {/* -------- KPI row -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.5 }}>
                        {[
                            { label: 'Total Inventory', value: lastTotals.total.toLocaleString(), meta: 'Beds', icon: '🛏️' },
                            {
                                label: 'Occupied / Partial',
                                value: `${lastTotals.occ.toLocaleString()} / ${lastTotals.partial.toLocaleString()}`,
                                meta: `${lastTotals.pct.toFixed(1)}% Occ • ${lastTotals.partialPct.toFixed(1)}% Partial • ${months[lastIdx]?.label}`,
                                icon: '✅'
                            },
                            { label: 'Booked', value: lastTotals.booked.toLocaleString(), meta: `${lastTotals.bookedPct.toFixed(1)}% of inventory`, icon: '📌' },
                            { label: 'Available', value: lastTotals.bal.toLocaleString(), meta: `${lastTotals.availPct.toFixed(1)}% free`, icon: '🟢' },
                            { label: 'Avg Occupancy', value: `${avgOcc.toFixed(1)}%`, meta: `${lastTotals.pct >= firstTotals.pct ? '▲ up' : '▼ down'} vs range start`, icon: '📊', trendUp: lastTotals.pct >= firstTotals.pct },
                            { label: 'Peak Occupancy', value: `${peakPct.toFixed(1)}%`, meta: peakMonth, icon: '🔥' },
                        ].map((k, i) => (
                            <Grid item xs={6} md={2} key={i}>
                                <Paper elevation={0} sx={{ ...cardSx, p: 1.5, position: 'relative', overflow: 'hidden' }}>
                                    <Box sx={{ position: 'absolute', top: -18, right: -18, width: 64, height: 64, borderRadius: '50%', background: KPI_ACCENTS[i], opacity: 0.14 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: COLORS.textMuted }}>{k.label}</Typography>
                                        <Box sx={{ fontSize: '0.95rem' }}>{k.icon}</Box>
                                    </Box>
                                    <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.03em' }}>{k.value}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'trendUp' in k ? (k.trendUp ? COLORS.success : COLORS.danger) : COLORS.textMuted, mt: 0.4, fontWeight: 'trendUp' in k ? 700 : 400 }}>{k.meta}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={1.8} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} md={8}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                        Occupancy Heatmap
                                    </Typography>
                                </Box>

                                <Box sx={{ p: 2, overflowX: 'auto' }}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `110px repeat(${months.length}, minmax(60px, 80px))`,
                                            gap: '3px',
                                            width: 'fit-content',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {/* Month Headers */}
                                        <Box />

                                        {months.map((m) => (
                                            <Typography
                                                key={m.label}
                                                sx={{
                                                    fontSize: '0.62rem',
                                                    fontWeight: 600,
                                                    color: COLORS.textMuted,
                                                    textAlign: 'center',
                                                    pb: 0.4,
                                                }}
                                            >
                                                {m.label}
                                            </Typography>
                                        ))}

                                        {/* Individual Bed Type Rows */}
                                        {filteredSeries.map((s) => (
                                            <React.Fragment key={`${s.roomType}-${s.bedType}`}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.82rem',
                                                        fontWeight: 600,
                                                        color: COLORS.textSecondary,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {s.bedType}
                                                </Typography>

                                                {s.occupiedArr.map((o, i) => {
                                                    const total = Number(s.totalArr[i] || 0);
                                                    const partial = Number(s.partialArr?.[i] || 0);

                                                    // Occupied + Partial
                                                    const occupancy = o + partial;

                                                    const pct = total
                                                        ? (occupancy / total) * 100
                                                        : 0;

                                                    const bg =
                                                        pct >= 50
                                                            ? `rgba(248,113,113,${0.4 + pct / 180})`
                                                            : pct >= 25
                                                                ? `rgba(251,191,36,${0.35 + pct / 180})`
                                                                : pct >= 5
                                                                    ? `rgba(52,211,153,${0.3 + pct / 140})`
                                                                    : 'rgba(45,38,64,0.95)';

                                                    return (
                                                        <Box
                                                            key={`${s.roomType}-${s.bedType}-${i}`}
                                                            title={`${months[i].label}: ${o} occupied + ${partial} partial (${pct.toFixed(1)}%)`}
                                                            sx={{
                                                                background: bg,
                                                                borderRadius: '5px',
                                                                aspectRatio: '1.35',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#fff',
                                                                lineHeight: 1.1,
                                                            }}
                                                        >
                                                            {/* Occupied + Partial */}
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {occupancy % 1 === 0
                                                                    ? occupancy
                                                                    : occupancy.toFixed(1)}
                                                            </Typography>

                                                            {/* Percentage */}
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.62rem',
                                                                    fontWeight: 500,
                                                                    opacity: 0.9,
                                                                    mt: 0.3,
                                                                }}
                                                            >
                                                                {pct.toFixed(0)}%
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}

                                        {/* Total Row */}
                                        <Typography
                                            sx={{
                                                fontSize: '0.9rem',
                                                fontWeight: 700,
                                                color: COLORS.text,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            Total
                                        </Typography>

                                        {months.map((m, i) => {
                                            // Total Occupied
                                            const totalOccupied = filteredSeries.reduce(
                                                (sum, s) =>
                                                    sum + Number(s.occupiedArr[i] || 0),
                                                0
                                            );

                                            // Total Partial
                                            const totalPartial = filteredSeries.reduce(
                                                (sum, s) =>
                                                    sum + Number(s.partialArr?.[i] || 0),
                                                0
                                            );

                                            // Total Beds
                                            const totalBeds = filteredSeries.reduce(
                                                (sum, s) =>
                                                    sum + Number(s.totalArr[i] || 0),
                                                0
                                            );

                                            // Occupied + Partial
                                            const totalOccupancy =
                                                totalOccupied + totalPartial;

                                            // Percentage
                                            const pct = totalBeds
                                                ? (totalOccupancy / totalBeds) * 100
                                                : 0;

                                            const bg =
                                                pct >= 50
                                                    ? `rgba(248,113,113,${0.4 + pct / 180})`
                                                    : pct >= 25
                                                        ? `rgba(251,191,36,${0.35 + pct / 180})`
                                                        : pct >= 5
                                                            ? `rgba(52,211,153,${0.3 + pct / 140})`
                                                            : 'rgba(45,38,64,0.95)';

                                            return (
                                                <Box
                                                    key={`total-${i}`}
                                                    title={`${m.label}: ${totalOccupied} occupied + ${totalPartial} partial (${pct.toFixed(1)}%)`}
                                                    sx={{
                                                        background: bg,
                                                        borderRadius: '5px',
                                                        aspectRatio: '1.35',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#fff',
                                                        border: `1px solid ${COLORS.primaryGlow}`,
                                                        lineHeight: 1.1,
                                                    }}
                                                >
                                                    {/* Total Occupied + Partial */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {totalOccupancy % 1 === 0
                                                            ? totalOccupancy
                                                            : totalOccupancy.toFixed(1)}
                                                    </Typography>

                                                    {/* Percentage */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            fontWeight: 500,
                                                            opacity: 0.9,
                                                            mt: 0.3,
                                                        }}
                                                    >
                                                        {pct.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Bed Status Wise %</Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted }}>Occupied • Available • Booked</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ p: 2, height: 260 }}>
                                    {donutData && <Doughnut data={donutData} options={donutOptions} />}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* -------- Trend + Donut -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={12}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Occupancy & Availability Trend</Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted }}>Toggle metric to explore the range</Typography>
                                    </Box>
                                    <ToggleButtonGroup size="small" exclusive value={trendMetric} onChange={(e, v) => v && setTrendMetric(v)} sx={{ background: COLORS.bg, borderRadius: '8px', p: '3px' }}>
                                        {[['occupancy', 'Occ %'], ['available', 'Avail %'], ['bookedpct', 'Booked %'], ['occupied', 'Count']].map(([val, lbl]) => (
                                            <ToggleButton key={val} value={val} sx={{ color: COLORS.textMuted, textTransform: 'none', border: 0, borderRadius: '6px !important', px: 1.2, fontSize: '0.7rem', '&.Mui-selected': { background: COLORS.primary, color: '#fff' } }}>{lbl}</ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                                <Box sx={{ p: 2, height: 300 }}>
                                    {trendChartData && <Line data={trendChartData} options={trendOptions} />}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                        <Box sx={headerSx}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Full Availability Matrix</Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.textMuted }}>{months[0]?.label} → {months[lastIdx]?.label}</Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 440 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {['Room', 'Bed', 'Metric', ...months.map(m => m.label)].map((h, i) => (
                                            <TableCell key={i} sx={{ background: '#120e1c', color: COLORS.textMuted, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: `1px solid ${COLORS.cardBorder}`, textAlign: i < 3 ? 'left' : 'center' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredSeries.map((s) => {
                                        const metrics = [
                                            { key: 'Total Bed', vals: s.totalArr, pct: false },
                                            { key: 'Booked', vals: s.bookedArr, pct: false },
                                            { key: 'Occupied', vals: s.occupiedArr, pct: false },
                                            { key: 'Balance', vals: s.balanceArr, pct: false },
                                            { key: 'Partial', vals: s.partialArr, pct: false },
                                            { key: 'Occupancy %', vals: s.occupiedArr.map((o, i) => (s.totalArr[i] ? (o / s.totalArr[i]) * 100 : 0)), pct: true },
                                            { key: 'Booked %', vals: s.bookedArr.map((b, i) => (s.totalArr[i] ? (b / s.totalArr[i]) * 100 : 0)), pct: true },
                                            { key: 'Available %', vals: s.balanceArr.map((b, i) => (s.totalArr[i] ? (b / s.totalArr[i]) * 100 : 0)), pct: true }
                                        ];
                                        return metrics.map((m, mi) => (
                                            <TableRow key={`${s.roomType}-${s.bedType}-${m.key}`} sx={{ '& td': { borderBottom: `1px solid ${COLORS.cardBorder}`, color: COLORS.textMuted, fontSize: '0.74rem' } }}>
                                                {mi === 0 && (
                                                    <>
                                                        <TableCell rowSpan={metrics.length} sx={{ verticalAlign: 'middle', fontWeight: 600, color: `${COLORS.text} !important` }}>{s.roomType}</TableCell>
                                                        <TableCell rowSpan={metrics.length} sx={{ verticalAlign: 'middle', fontWeight: 600, color: `${COLORS.text} !important` }}>{s.bedType}</TableCell>
                                                    </>
                                                )}
                                                <TableCell sx={{ textAlign: 'left', fontWeight: 500, color: `${COLORS.text} !important` }}>{m.key}</TableCell>
                                                {m.vals.map((v, i) => (
                                                    <TableCell key={i} sx={{ textAlign: 'center' }}>
                                                        {m.pct ? <Box component="span" sx={pctBadge(v)}>{v.toFixed(1)}%</Box> : Math.round(v).toLocaleString()}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ));
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* -------- Three bar/pie charts -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Occupied Beds</Typography></Box>
                                <Box sx={{ p: 1.5, height: 230 }}>{barOccupiedData && <Bar data={barOccupiedData} options={barOptions} />}</Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Booked vs Available</Typography></Box>
                                <Box sx={{ p: 1.5, height: 230 }}>{barBookedAvailData && <Bar data={barBookedAvailData} options={barOptions} />}</Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* -------- Health status -------- */}
                    <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Health Status</Typography></Box>
                                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                                    {[
                                        statusFor('Occupancy', lastTotals.pct, 'occ'),
                                        statusFor('Available %', lastTotals.availPct, 'avail'),
                                        statusFor('Booked %', lastTotals.bookedPct, 'booked')
                                    ].map((row, i) => (
                                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.label}</Typography>
                                            <Chip size="small" label={`${row.value.toFixed(1)}%`} sx={{ background: `${row.color}26`, color: row.color, fontWeight: 700 }} />
                                        </Box>
                                    ))}
                                    <Divider sx={{ borderColor: COLORS.cardBorder }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Trend</Typography>
                                        <Chip size="small" label={lastTotals.pct < firstTotals.pct ? 'Declining' : 'Stable / Up'} sx={{ background: lastTotals.pct < firstTotals.pct ? `${COLORS.danger}26` : `${COLORS.success}26`, color: lastTotals.pct < firstTotals.pct ? COLORS.danger : COLORS.success, fontWeight: 700 }} />
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={cardSx}>
                                <Box sx={{ p: 0, height: 270 }}>
                                    <Box sx={headerSx}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Bed Type Share</Typography></Box>
                                    <Box sx={{ p: 1.5, height: 230 }}>{pieBedTypeData && <Pie data={pieBedTypeData} options={pieOptions} />}</Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Typography sx={{ textAlign: 'center', fontSize: '0.7rem', color: COLORS.textMuted, mt: 2.5, pt: 1.2, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                        Occupancy % = Occupied ÷ Total • Available % = Balance ÷ Total • Booked % = Booked ÷ Total
                    </Typography>
                </>
            )}
        </Box>
    );
}